import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronRight, ChevronDown, Copy, Search, MoonStar, AlertTriangle, CheckCircle2, Repeat,
  FileDown, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const ESTADOS = [
  ['dormido', 'Dejaron de cargar', MoonStar, 'text-red-600 dark:text-red-400', 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'],
  ['en_riesgo', 'En riesgo', AlertTriangle, 'text-amber-600 dark:text-amber-400', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'],
  ['activo', 'Activos', CheckCircle2, 'text-emerald-600 dark:text-emerald-400', 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'],
  ['intermitente', 'Van y vienen', Repeat, 'text-sky-600 dark:text-sky-400', 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300'],
];

const avisoTexto = (r) => {
  const club = r.club_principal?.club || 'tu club';
  const ritmo = r.frecuencia_dias <= 1.5 ? 'casi todos los días' : `cada ${Math.round(r.frecuencia_dias)} días`;
  return `Aviso EVOGAME · ${club}\n`
    + `${r.titular}${r.cuil ? ` (${r.cuil})` : ''} era un cargador habitual: ${ritmo}, `
    + `${r.envios_n} cargas por un total de ${r.monto_total.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}.\n`
    + `Su última carga fue el ${r.ultimo_envio} (hace ${r.dias_sin_enviar} días).\n`
    + `Movía alrededor de ${r.monto_mensual.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })} por mes.`
    + (r.otros_clubes.length ? `\nTambién cargó en: ${r.otros_clubes.join(', ')}.` : '');
};

/** Remitentes habituales que dejaron de cargar, están por irse o van y vienen. */
export const ClientesDormidos = ({ carteraId, clienteId, carteras, clubes, onCartera, onClub, formatMonto }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState('dormido');
  const [buscar, setBuscar] = useState('');
  const [query, setQuery] = useState('');
  const [abiertos, setAbiertos] = useState(() => new Set());
  const [exportando, setExportando] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(buscar), 400);
    return () => clearTimeout(t);
  }, [buscar]);

  const cargar = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (carteraId) p.append('cartera_id', carteraId);
    if (clienteId) p.append('cliente_id', clienteId);
    if (estado) p.append('estado', estado);
    if (query) p.append('search', query);
    axios.get(`${API}/analytics/remitentes-actividad?${p}`, auth())
      .then(r => { if (r.data?.success) setData(r.data); })
      .catch(() => toast.error('Error al calcular la actividad'))
      .finally(() => setLoading(false));
  }, [carteraId, clienteId, estado, query]);

  useEffect(() => { cargar(); }, [cargar]);

  const copiar = async (r) => {
    try {
      await navigator.clipboard.writeText(avisoTexto(r));
      toast.success(`Aviso de ${r.titular} copiado`);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const toggle = (k) => setAbiertos(prev => {
    const n = new Set(prev);
    n.has(k) ? n.delete(k) : n.add(k);
    return n;
  });

  const exportar = async (key, label) => {
    setExportando(key);
    const p = new URLSearchParams({ estado: key });
    if (carteraId) p.append('cartera_id', carteraId);
    if (clienteId) p.append('cliente_id', clienteId);
    if (query) p.append('search', query);
    try {
      const res = await axios.get(`${API}/analytics/export-actividad-excel?${p}`,
        { ...auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${key}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Excel de "${label}" descargado`);
    } catch {
      toast.error(`No se pudo exportar "${label}"`);
    } finally {
      setExportando(null);
    }
  };

  const res = data?.resumen;
  const inputCls = 'px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500';

  return (
    <div className="space-y-5" data-testid="clientes-dormidos">
      {/* Resumen por estado: también funcionan como filtro */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {ESTADOS.map(([key, label, Icon, color, badge]) => {
          const info = key === 'intermitente'
            ? { cantidad: res?.intermitentes ?? 0, monto_mensual: null }
            : res?.[key];
          const activo = estado === key;
          return (
            <div key={key}
              className={`relative text-left rounded-2xl border p-4 transition-colors ${activo
                ? 'border-violet-400 dark:border-violet-500 bg-violet-50/70 dark:bg-violet-900/20'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'}`}>
              <button onClick={() => setEstado(key)} className="w-full text-left"
                data-testid={`estado-${key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${color}`} />
                  <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{info?.cantidad ?? 0}</p>
                {info?.monto_mensual != null && (
                  <p className="text-[11px] text-gray-500 mt-0.5">{formatMonto(info.monto_mensual)} por mes en juego</p>
                )}
              </button>
              <button onClick={() => exportar(key, label)} disabled={exportando === key}
                title={`Exportar "${label}" a Excel`}
                className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 disabled:opacity-40 transition-colors"
                data-testid={`estado-export-${key}`}>
                {exportando === key
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <FileDown className="w-4 h-4" />}
              </button>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-5 pb-3 flex flex-wrap items-end gap-3">
          <div className="mr-auto">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {ESTADOS.find(e => e[0] === estado)?.[1]}
            </h3>
            <p className="text-xs text-gray-500">
              {data?.total ?? 0} de tus cargadores habituales (4 cargas o más).{' '}
              {data?.criterio && <span>Dormido = {data.criterio.dormido}.</span>}
            </p>
          </div>
          <select value={carteraId} onChange={(e) => onCartera(e.target.value)} className={inputCls}
            data-testid="dormidos-filtro-cartera">
            <option value="">Todas las carteras</option>
            {carteras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={clienteId} onChange={(e) => onClub(e.target.value)} className={inputCls}
            data-testid="dormidos-filtro-club">
            <option value="">{carteraId ? 'Todos los clubes de la cartera' : 'Todos los clubes'}</option>
            {clubes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar titular o CUIL…"
              className={`${inputCls} pl-9 w-64`} data-testid="dormidos-buscar" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr className="text-[10px] uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2 w-8" />
                <th className="px-3 py-2 text-left">Titular</th>
                <th className="px-3 py-2 text-left">Club principal</th>
                <th className="px-3 py-2 text-center">Cargaba</th>
                <th className="px-3 py-2 text-center">Sin cargar</th>
                <th className="px-3 py-2 text-center">Cargas</th>
                <th className="px-3 py-2 text-right">Por mes</th>
                <th className="px-3 py-2 text-right">Total histórico</th>
                <th className="px-3 py-2 text-center">Aviso</th>
              </tr>
            </thead>
            <tbody data-testid="dormidos-tbody">
              {loading ? (
                <tr><td colSpan="9" className="px-3 py-10 text-center text-sm text-gray-400">Analizando el historial…</td></tr>
              ) : (data?.remitentes || []).length === 0 ? (
                <tr><td colSpan="9" className="px-3 py-10 text-center text-sm text-gray-400">No hay nadie en este grupo. Buena señal.</td></tr>
              ) : data.remitentes.map((r) => {
                const clave = `${r.titular}|${r.cuil || ''}`;
                const abierto = abiertos.has(clave);
                const Icon = abierto ? ChevronDown : ChevronRight;
                return (
                  <React.Fragment key={clave}>
                    <tr onClick={() => toggle(clave)}
                      className={`border-t border-gray-100 dark:border-gray-700 cursor-pointer transition-colors ${abierto
                        ? 'bg-violet-50/60 dark:bg-violet-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
                      data-testid={`dormido-fila-${r.titular}`}>
                      <td className="px-3 py-2"><Icon className="w-4 h-4 text-violet-600 dark:text-violet-400" /></td>
                      <td className="px-3 py-2">
                        <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{r.titular}</span>
                        {r.intermitente && (
                          <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 font-semibold">
                            va y viene · {r.pausas} pausas
                          </span>
                        )}
                        <span className="block text-[10px] text-gray-400 font-mono">
                          {r.cuil || 'sin CUIL'}
                          {r.cuil_ambiguo && (
                            <span title="El CUIL del comprobante aparece con nombres de personas distintas (posible error de lectura). Se separó por nombre."
                              className="ml-1 inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400 not-italic"
                              data-testid={`dormido-cuil-dudoso-${r.titular}`}>
                              <AlertTriangle className="w-3 h-3" />CUIL dudoso
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                        {r.club_principal?.club || '—'}
                        {r.otros_clubes.length > 0 && (
                          <span className="block text-[10px] text-gray-400">+{r.otros_clubes.length} club(es) más</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-gray-500">
                        {r.frecuencia_dias <= 1.5 ? 'casi a diario' : `cada ${Math.round(r.frecuencia_dias)} días`}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[11px] font-semibold tabular-nums ${r.estado === 'dormido'
                          ? 'text-red-600 dark:text-red-400' : r.estado === 'en_riesgo'
                            ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          {r.dias_sin_enviar} días
                        </span>
                        <span className="block text-[10px] text-gray-400 font-mono">{r.ultimo_envio}</span>
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-gray-600 dark:text-gray-400 tabular-nums">{r.envios_n}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono font-semibold text-gray-900 dark:text-gray-100">{formatMonto(r.monto_mensual)}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono text-emerald-700 dark:text-emerald-400">{formatMonto(r.monto_total)}</td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={(e) => { e.stopPropagation(); copiar(r); }}
                          className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 hover:text-violet-600 hover:border-violet-400 transition-colors"
                          title="Copiar aviso para la agencia" data-testid={`copiar-aviso-${r.titular}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                    {abierto && (
                      <tr className="bg-gray-50/80 dark:bg-gray-900/40">
                        <td colSpan="9" className="px-3 py-3">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid={`dormido-detalle-${r.titular}`}>
                            <div className="lg:col-span-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                              <p className="px-3 py-2 text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-100 dark:border-gray-700">
                                Últimas cargas ({r.envios.length} de {r.envios_n})
                              </p>
                              <div className="max-h-56 overflow-y-auto">
                                <table className="w-full">
                                  <tbody>
                                    {r.envios.map((e, i) => (
                                      <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                                        <td className="px-3 py-1.5 text-[11px] font-mono text-gray-600 dark:text-gray-300">{e.date}</td>
                                        <td className="px-3 py-1.5 text-[11px] text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">{formatMonto(e.amount)}</td>
                                        <td className="px-3 py-1.5 text-[11px] text-gray-500">{e.cliente_name || '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-xs space-y-1">
                                <p className="text-gray-500">Primera carga: <span className="text-gray-900 dark:text-gray-100 font-mono">{r.primer_envio}</span></p>
                                <p className="text-gray-500">Última carga: <span className="text-gray-900 dark:text-gray-100 font-mono">{r.ultimo_envio}</span></p>
                                <p className="text-gray-500">Promedio por carga: <span className="text-gray-900 dark:text-gray-100">{formatMonto(r.promedio_envio)}</span></p>
                                {r.pausa_mas_larga > 0 && (
                                  <p className="text-gray-500">Pausa más larga: <span className="text-gray-900 dark:text-gray-100">{r.pausa_mas_larga} días</span></p>
                                )}
                                {r.nombres_alternativos.length > 0 && (
                                  <p className="text-gray-500">También aparece como: <span className="text-gray-900 dark:text-gray-100">{r.nombres_alternativos.join(' · ')}</span></p>
                                )}
                              </div>
                              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                                <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">Cargó en</p>
                                {r.clubes_detalle.map(c => (
                                  <div key={c.club} className="flex items-center justify-between text-xs py-0.5">
                                    <span className="text-gray-700 dark:text-gray-300">{c.club}</span>
                                    <span className="font-mono text-gray-500">{c.envios} · {formatMonto(c.monto)}</span>
                                  </div>
                                ))}
                              </div>
                              <button onClick={() => copiar(r)}
                                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium transition-colors">
                                <Copy className="w-3.5 h-3.5" /> Copiar aviso para la agencia
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClientesDormidos;
